(function () {
  'use strict';

  const CONFIG_ID = 'CustomRebuyShippingEstimatorConfig';
  const ROOT_SELECTOR = '[data-custom-rebuy-shipping-estimator]';
  const MODAL_SELECTOR = '[data-custom-rebuy-shipping-estimator-modal]';
  const MODAL_HOST_CLASS = 'custom-rebuy-shipping-estimator__modal-host';
  const REBUY_ROOT_SELECTORS = [
    '.rebuy-cart__flyout--inner',
    '.rebuy-cart__flyout',
  ];
  const EVENTS = ['rebuy:smartcart.show', 'rebuy:smartcart.ready', 'rebuy:cart:change'];
  const DEFAULT_STRINGS = {
    summaryLabel: 'Shipping cost',
    summaryAction: 'Click to estimate',
    title: 'Estimate shipping rates',
    close: 'Close',
    countryLabel: 'Country/region',
    provinceLabel: 'State / province',
    zipLabel: 'Postal/ZIP code',
    calculate: 'Calculate',
    loading: 'Loading shipping rates...',
  };

  function readConfig() {
    const node = document.getElementById(CONFIG_ID);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[Custom Rebuy Shipping Estimator] Invalid config JSON', error);
      return null;
    }
  }

  function createCountrySelectFromMarkup(countryOptionTags) {
    const select = document.createElement('select');
    select.innerHTML = countryOptionTags || '';
    return select;
  }

  function readProvinceData(option) {
    const raw = option?.getAttribute('data-provinces');
    if (!raw) return [];

    try {
      return JSON.parse(raw);
    } catch (error) {
      console.warn('[Custom Rebuy Shipping Estimator] Invalid province data', error);
      return [];
    }
  }

  function readStoredAddress(storageKey) {
    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function writeStoredAddress(storageKey, address) {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(address));
    } catch (error) {
      console.warn('[Custom Rebuy Shipping Estimator] Unable to persist address', error);
    }
  }

  function buildFetchConfig(body, signal) {
    const baseConfig = window.theme?.utils?.fetchConfig ? window.theme.utils.fetchConfig('javascript') : {};
    const baseHeaders = baseConfig.headers || {};

    return {
      ...baseConfig,
      method: 'POST',
      headers: {
        ...baseHeaders,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body,
      signal,
    };
  }

  function buildShippingRatesUrl() {
    const cartUrl = window.theme?.routes?.cart_url || '/cart';

    try {
      const shippingRatesUrl = new URL(cartUrl, window.location.origin);
      shippingRatesUrl.pathname = `${shippingRatesUrl.pathname.replace(/\/+$/, '')}/shipping_rates.json`;
      shippingRatesUrl.search = '';
      shippingRatesUrl.hash = '';
      return shippingRatesUrl.toString();
    } catch (error) {
      const normalizedCartPath = String(cartUrl || '/cart').replace(/\/+$/, '') || '/cart';
      return `${normalizedCartPath}/shipping_rates.json`;
    }
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function normalizeErrorMessages(value, fallbackMessage) {
    if (value == null || value === '') return [];

    if (Array.isArray(value)) {
      return value.flatMap((entry) => normalizeErrorMessages(entry, fallbackMessage));
    }

    if (typeof value === 'string') {
      return value === fallbackMessage ? [] : [value];
    }

    if (typeof value === 'object') {
      return Object.values(value).flatMap((entry) => normalizeErrorMessages(entry, fallbackMessage));
    }

    return [];
  }

  function renderMessageList(messages) {
    if (!messages.length) return '';

    return `<ul class="list-disc grid gap-2" role="list">${messages.map((message) => `<li>${escapeHtml(message)}</li>`).join('')}</ul>`;
  }

  function buildStrings(config) {
    const shippingStrings = window.theme?.shippingCalculatorStrings || {};

    return {
      ...DEFAULT_STRINGS,
      error: shippingStrings.error || 'One or more errors occurred while retrieving the shipping rates:',
      notFound: shippingStrings.notFound || 'Sorry, we do not ship to your address.',
      oneResult: shippingStrings.oneResult || 'There is one shipping rate for your address:',
      multipleResults: shippingStrings.multipleResults || 'There are multiple shipping rates for your address:',
      ...(config?.strings || {}),
    };
  }

  class CustomRebuyShippingEstimator {
    constructor(config) {
      this.config = config;
      this.strings = buildStrings(config);
      this.ui = window.CustomRebuyShippingEstimatorUI;
      this.mountTimeouts = [];
      this.handleLifecycleEvent = this.handleLifecycleEvent.bind(this);
    }

    init() {
      if (!this.ui?.renderMarkup) {
        console.warn('[Custom Rebuy Shipping Estimator] UI helpers are unavailable');
        return;
      }

      EVENTS.forEach((eventName) => {
        document.addEventListener(eventName, this.handleLifecycleEvent);
      });

      this.scheduleMountAttempts();
    }

    handleLifecycleEvent() {
      this.scheduleMountAttempts();
    }

    scheduleMountAttempts() {
      this.mountTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
      this.mountTimeouts = [0, 120, 300].map((delay) => {
        return window.setTimeout(() => {
          window.requestAnimationFrame(() => this.mount());
        }, delay);
      });
    }

    findRebuyRoot() {
      const subtotalComponent = document.querySelector('[data-rebuy-component="cart-subtotal"]');

      if (subtotalComponent) {
        return subtotalComponent.closest('.rebuy-cart__flyout--inner')
          || subtotalComponent.closest('.rebuy-cart__flyout')
          || null;
      }

      return REBUY_ROOT_SELECTORS.map((selector) => document.querySelector(selector)).find(Boolean) || null;
    }

    findAnchor(rebuyRoot) {
      if (!rebuyRoot) return null;

      const selectors = this.config?.selectors?.subtotal || [];
      const primary = selectors.map((selector) => {
        if (rebuyRoot.matches?.(selector)) return rebuyRoot;
        return rebuyRoot.querySelector(selector);
      }).find(Boolean);

      if (primary) return primary;

      return rebuyRoot.querySelector('.rebuy-cart__flyout-actions, .rebuy-cart__actions, [data-rebuy-component="cart-subtotal"]');
    }

    resolveMountPoint(anchor) {
      const subtotalComponent = anchor.closest('[data-rebuy-component="cart-subtotal"]');

      if (subtotalComponent?.parentNode) {
        return {
          parent: subtotalComponent.parentNode,
          reference: subtotalComponent.nextSibling,
        };
      }

      return {
        parent: anchor.parentNode,
        reference: anchor,
      };
    }

    cleanupDetachedNodes() {
      document.querySelectorAll(ROOT_SELECTOR).forEach((node) => node.remove());
      document.querySelectorAll(MODAL_SELECTOR).forEach((node) => node.remove());
    }

    createIds() {
      const suffix = Math.random().toString(36).slice(2, 8);

      return {
        country: `CustomRebuyShippingCountry-${suffix}`,
        modal: `CustomRebuyShippingModal-${suffix}`,
        province: `CustomRebuyShippingProvince-${suffix}`,
        zip: `CustomRebuyShippingZip-${suffix}`,
      };
    }

    renderMarkup() {
      this.ids = this.ids || this.createIds();

      return this.ui.renderMarkup({
        ids: this.ids,
        strings: this.strings,
      });
    }

    getElements() {
      const modal = document.getElementById(this.ids.modal);

      return {
        form: modal?.querySelector('[data-estimator-form]'),
        modal,
        results: modal?.querySelector('[data-estimator-results]'),
        submitButton: modal?.querySelector('[data-estimator-submit]'),
        countrySelect: modal?.querySelector(`#${this.ids.country}`),
        provinceSelect: modal?.querySelector(`#${this.ids.province}`),
        provinceField: modal?.querySelector('[data-estimator-province-field]'),
        zipInput: modal?.querySelector(`#${this.ids.zip}`),
      };
    }

    resolveModalHost(anchor, fallbackParent) {
      const modalHost = anchor.closest('.rebuy-cart__column--primary')
        || anchor.closest('[data-rebuy-cart-anchor="footer"]')
        || fallbackParent;

      modalHost?.classList?.add(MODAL_HOST_CLASS);
      return modalHost;
    }

    hoistModal(root, anchor, fallbackParent) {
      const modal = root.querySelector(MODAL_SELECTOR);
      if (!modal) return null;

      const modalHost = this.resolveModalHost(anchor, fallbackParent);
      if (modal.parentNode !== modalHost) {
        modalHost.appendChild(modal);
      }

      return modal;
    }

    hydrateAddressFields() {
      const {
        countrySelect,
        provinceSelect,
        provinceField,
        zipInput,
      } = this.getElements();
      if (!countrySelect || !provinceSelect || !provinceField || !zipInput) return;

      const sourceSelect = createCountrySelectFromMarkup(this.config.countryOptionTags);
      const storedAddress = readStoredAddress(this.config.storageKey) || {};
      const defaultAddress = this.config.defaultAddress || {};
      const initialAddress = {
        country: storedAddress.country || defaultAddress.country || '',
        province: storedAddress.province || defaultAddress.province || '',
        provinceCode: storedAddress.provinceCode || '',
        zip: storedAddress.zip || defaultAddress.zip || '',
      };

      countrySelect.innerHTML = sourceSelect.innerHTML;

      if (initialAddress.country) {
        const countryIndex = Array.from(countrySelect.options).findIndex((option) => {
          return option.value === initialAddress.country || option.textContent === initialAddress.country;
        });

        if (countryIndex >= 0) {
          countrySelect.selectedIndex = countryIndex;
        }
      }

      if (initialAddress.zip) {
        zipInput.value = initialAddress.zip;
      }

      provinceSelect.dataset.preferredProvince = initialAddress.province;
      provinceSelect.dataset.preferredProvinceCode = initialAddress.provinceCode;

      const syncProvinceOptions = () => {
        const selectedOption = countrySelect.options[countrySelect.selectedIndex];
        const provinces = readProvinceData(selectedOption);
        const preferredProvince = provinceSelect.dataset.preferredProvince || '';
        const preferredProvinceCode = provinceSelect.dataset.preferredProvinceCode || '';

        provinceSelect.innerHTML = '';
        provinceField.hidden = provinces.length === 0;

        if (provinces.length === 0) {
          return;
        }

        provinces.forEach(([code, label]) => {
          const isSelected = preferredProvinceCode === code || preferredProvince === label || preferredProvince === code;
          provinceSelect.options.add(new Option(label, code, isSelected, isSelected));
        });
      };

      countrySelect.addEventListener('change', () => {
        provinceSelect.dataset.preferredProvince = '';
        provinceSelect.dataset.preferredProvinceCode = '';
        syncProvinceOptions();
      });

      syncProvinceOptions();
    }

    persistCurrentAddress() {
      const { countrySelect, provinceSelect, zipInput } = this.getElements();
      if (!countrySelect || !provinceSelect || !zipInput) return;

      const countryLabel = countrySelect.options[countrySelect.selectedIndex]?.value || '';
      const provinceLabel = provinceSelect.options[provinceSelect.selectedIndex]?.textContent || '';

      writeStoredAddress(this.config.storageKey, {
        country: countryLabel,
        province: provinceLabel,
        provinceCode: provinceSelect.value || '',
        zip: zipInput.value.trim(),
      });
    }

    bindRoot() {
      const { form, zipInput } = this.getElements();
      if (!form || !zipInput) return;

      form.addEventListener('change', () => this.persistCurrentAddress());
      zipInput.addEventListener('input', () => this.persistCurrentAddress());
      form.addEventListener('submit', (event) => this.handleSubmit(event));
    }

    async handleSubmit(event) {
      event.preventDefault();

      const {
        submitButton,
        results,
        countrySelect,
        provinceSelect,
        zipInput,
      } = this.getElements();
      if (!submitButton || !results || !countrySelect || !provinceSelect || !zipInput) return;

      const countryValue = countrySelect.options[countrySelect.selectedIndex]?.value || '';

      const body = JSON.stringify({
        shipping_address: {
          zip: zipInput.value.trim(),
          country: countryValue,
          province: provinceSelect.value || '',
          province_code: provinceSelect.value || '',
        },
      });

      this.abortController?.abort();
      this.abortController = new AbortController();

      submitButton.setAttribute('aria-busy', 'true');
      results.hidden = false;
      results.className = 'custom-rebuy-shipping-estimator__results alert alert--info';
      results.innerHTML = `<p>${this.strings.loading}</p>`;

      this.persistCurrentAddress();

      try {
        const response = await fetch(buildShippingRatesUrl(), buildFetchConfig(body, this.abortController.signal));
        const payload = await response.json();

        if (payload.shipping_rates) {
          this.renderShippingRates(results, payload.shipping_rates);
        }
        else {
          this.renderShippingErrors(results, payload);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        this.renderShippingErrors(results, null);
      } finally {
        submitButton.removeAttribute('aria-busy');
      }
    }

    renderShippingRates(container, shippingRates) {
      const listMarkup = renderMessageList(shippingRates.map(({ presentment_name, currency, price }) => {
        return `${presentment_name}: ${currency} ${price}`;
      }));

      const intro = shippingRates.length === 1
        ? this.strings.oneResult
        : shippingRates.length === 0
          ? this.strings.notFound
          : this.strings.multipleResults;

      container.className = shippingRates.length === 0
        ? 'custom-rebuy-shipping-estimator__results alert alert--error'
        : 'custom-rebuy-shipping-estimator__results alert alert--success';
      container.hidden = false;
      container.innerHTML = `
        <p>${escapeHtml(intro)}</p>
        ${listMarkup}
      `;
    }

    renderShippingErrors(container, errors) {
      const listMarkup = renderMessageList(normalizeErrorMessages(errors, this.strings.error));

      container.className = 'custom-rebuy-shipping-estimator__results alert alert--error';
      container.hidden = false;
      container.innerHTML = `
        <p>${escapeHtml(this.strings.error)}</p>
        ${listMarkup}
      `;
    }

    mount() {
      if (!this.config?.enabled) return;

      const rebuyRoot = this.findRebuyRoot();
      if (!rebuyRoot) {
        this.cleanupDetachedNodes();
        return;
      }

      const anchor = this.findAnchor(rebuyRoot);
      if (!anchor || !anchor.parentNode) return;

      const mountPoint = this.resolveMountPoint(anchor);
      if (!mountPoint?.parent) return;

      const existingNodes = Array.from(document.querySelectorAll(ROOT_SELECTOR));
      let keptNode = null;

      existingNodes.forEach((node) => {
        if (node.parentNode !== mountPoint.parent) {
          const nodeModalId = node.dataset.modalId;
          const nodeModal = nodeModalId ? document.getElementById(nodeModalId) : null;
          nodeModal?.remove();
          node.remove();
          return;
        }

        if (!keptNode) {
          keptNode = node;
          return;
        }

        node.remove();
      });

      const existingRoot = mountPoint.parent.querySelector(ROOT_SELECTOR);
      if (existingRoot) {
        const existingModalId = existingRoot.dataset.modalId;
        const existingModal = existingModalId ? document.getElementById(existingModalId) : null;

        if (existingModal) {
          const modalHost = this.resolveModalHost(anchor, mountPoint.parent);
          if (existingModal.parentNode !== modalHost) {
            modalHost.appendChild(existingModal);
          }

          return;
        }

        existingRoot.remove();
      }

      document.querySelectorAll(MODAL_SELECTOR).forEach((modalNode) => modalNode.remove());

      const root = document.createElement('section');
      root.className = 'custom-rebuy-shipping-estimator';
      root.setAttribute('data-custom-rebuy-shipping-estimator', '');
      root.innerHTML = this.renderMarkup();
      root.dataset.modalId = this.ids.modal;

      mountPoint.parent.insertBefore(root, mountPoint.reference);
      this.hoistModal(root, anchor, mountPoint.parent);
      this.bindRoot();
      this.hydrateAddressFields();
    }
  }

  const config = readConfig();
  if (!config) return;

  new CustomRebuyShippingEstimator(config).init();
})();
